import { useQuery } from 'react-query';
import { Dispatch, SetStateAction } from 'react';
import { Edge, MarkerType, Node } from 'reactflow';
import { useTranslation } from 'react-i18next';

import { useApiContext } from 'context/ApiContext';
import { useGlobalContext } from 'context/GlobalContext';
import { KEYS } from 'helpers/react-query.config';
import { AxiosServiceError } from 'interfaces/general';
import { StepService } from 'api/client';
import { StatusItem } from 'api/catalog';

interface Props {
  workFlowOrderId?: number | string;
  workFlowId: number;
  setNodes: Dispatch<SetStateAction<Node[]>>;
  setEdges: Dispatch<SetStateAction<Edge[]>>;
  statusList: StatusItem[];
}

export const useGetGanttModal = ({
  workFlowOrderId = null,
  workFlowId = null,
  setNodes,
  setEdges,
  statusList
}: Props) => {
  const { client: clientApi } = useApiContext();
  const { showApiError } = useGlobalContext();
  const { t } = useTranslation('global');

  // Función recursiva para mapear los stepTickets
  const mapItemsToNodes = (
    stepService: StepService,
    parentNodeId: string | null,
    level: number,
    isError = false
  ): Node[] => {
    let nodes: Node[] = [];

    const id = `${
      stepService?.stepTicket?.ticketId || stepService.serviceId.toString()
    }`;
    const data = {
      label: isError
        ? `${stepService.serviceName} - ${stepService.message}`
        : stepService.serviceName
    };
    const position = {
      x: level * 250,
      y: 0
    };

    const color = isError
      ? '#a62520'
      : statusList?.find(
          (status) => status.id === stepService?.stepTicket?.ticketStatusId
        )?.hexColor || '#939393';

    const nodoPadre = parentNodeId;

    const newNode = {
      id: `${id}`,
      data,
      position,
      nodoPadre,
      style: {
        background: color,
        color: 'white',
        width: 175,
        height: 70,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: data.label.length > 60 ? '9px' : '12px'
      },
      selectable: !!(stepService?.stepTicket?.ticketId && !isError)
    };

    if (stepService.stepServices.length > 0) {
      newNode.sourcePosition = 'right';
    }

    if (parentNodeId) {
      newNode.targetPosition = 'left';
    }

    nodes.push(newNode);

    if (stepService.stepServices && stepService.stepServices.length > 0) {
      stepService.stepServices?.forEach((childStepService: StepService) => {
        nodes = nodes.concat(
          mapItemsToNodes(childStepService, `${id}`, level + 1, isError)
        );
      });
    }

    return nodes;
  };

  const mapEdges = (nodes: Node[]): Edge[] => {
    const edges: Edge[] = [];

    nodes?.forEach((node) => {
      if (node.nodoPadre) {
        edges.push({
          id: `${node.id}-${node.nodoPadre}`,
          source: node.nodoPadre,
          style: {
            stroke: 'black',
            strokeWidth: 1.5
          },
          markerEnd: {
            type: MarkerType.Circle,
            color: 'black'
          },
          target: node.id
        });
      }
    });

    return edges;
  };

  const orderNodes = (nodes: Node[], yBase = 0): Node[] => {
    // ordeno los nodos por position.x
    nodes?.sort((a, b) => a.position.x - b.position.x);

    // borro los nodos que están repetidos y dejo los que están más a la derecha
    const filteredNodes = nodes.filter(
      (node, index) => nodes.findIndex((n) => n.id === node.id) === index
    );

    filteredNodes?.forEach((node, index) => {
      node.position.y = index * 75 + yBase;
    });

    filteredNodes?.forEach((node) => {
      delete node.nodoPadre;
    });

    return filteredNodes;
  };

  const getGanttModal = useQuery(
    [KEYS.client.workFlowOrder.getGanttModal(), workFlowOrderId, workFlowId],
    () =>
      clientApi.workFlowOrder.getGanttDiagram({ workFlowOrderId, workFlowId }),
    {
      enabled: !!workFlowOrderId || !!workFlowId || statusList.length > 0,
      refetchOnWindowFocus: false,
      select: (data) => data.data.data,
      onError: (error: AxiosServiceError) => {
        if (error.response?.status === 404) {
          showApiError(error, t('errorMessages.noSteps'));
          return;
        }
        showApiError(error, t('common.commonMessages.genericRequestError'));
      },
      onSuccess: (data) => {
        let nodes = mapItemsToNodes(data.initialStepService, null, 0);

        const edges = mapEdges(nodes);

        let mistakeNodes = data?.misconfiguredServices?.map((service) =>
          mapItemsToNodes(service, null, 0, true)
        );

        const mistakeEdges = mapEdges(mistakeNodes?.flat());

        nodes = orderNodes(nodes);

        mistakeNodes = mistakeNodes?.map((mistakeNode, index) =>
          orderNodes(
            mistakeNode,
            (index === 0
              ? nodes.length
              : nodes.length +
                (mistakeNodes || []).slice(0, index).flat().length) * 75
          )
        );

        setNodes([...(mistakeNodes || []).flat(), ...nodes]);
        setEdges([...mistakeEdges, ...edges]);
      }
    }
  );

  return getGanttModal;
};
